import { FC, useMemo, useState, useContext } from "react";
import { Font,  Page, Text, View, Document, StyleSheet, pdf, Image } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import { InspectionOrder, InspectionOrderItem, InspectionOrderStatus } from "@/types/inspection";
import useInspactionEnum from "../../hooks/useInspactionEnum";
import { Button, notification } from "antd";
import { PrinterOutlined } from '@ant-design/icons';
import { DictContext } from "@/layout/context/DictContext";
import { getFileUrl } from "@/apis/file";
import { getUserName } from "@/utils/user";
import { dateFormat } from "@/utils/format";
import { getLocationIdx } from "@/utils/wLocation";
import dayjs from "dayjs";
import zioncom from 'images/system/zioncom.png';
import QRCode from "qrcode";
import { codeObjToStr, getParseCode, TagType } from "@/utils/codeParse";
import fontSrc from '@/assets/fonts/msyh.ttf';
import vnSrc from '@/assets/fonts/NotoSans-Regular.ttf';
import krSrc from '@/assets/fonts/NotoSansKR-Regular.otf';

// 注册支持中文的字体（重复注册幂等；若启动预热失败，首次渲染时在此兜底加载）
Font.register({
  family: "NotoSansSC",
  src: fontSrc
});

Font.register({
  family: "VnFontSrc",
  src: vnSrc
});

Font.register({
  family: "KoFontSrc",
  src: krSrc
});


// 定义 PDF 文档样式
const styles = StyleSheet.create({
  page: {
    position: "relative",
    fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"],
    padding: 28
  },
  titleSection: {
    marginBottom: 3
  },
  title: {
    fontSize: 14,
    fontWeight: 'bold',
    textAlign: 'center'
  },
  subTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    textAlign: 'center'
  },
  rowSection: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 3
  },
  rowItem: {
    flex: 1,
    marginRight: 15,
    fontSize: 6,
    textAlign: 'left',
    marginBottom: 2
  },
  table: {
    width: "100%",
    borderStyle: "solid",
    borderBottomWidth: 1,
    borderColor: "black",
    marginTop: 3
  },
  tableRow: {
    flexDirection: "row",
    borderStyle: "solid",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderTopWidth: 1,
    borderColor: "black",
  },
  tableCol: {
    borderStyle: "solid",
    borderRightWidth: 1,
    borderColor: "black",
    paddingHorizontal: 2,
    fontSize: 6,
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
    color: '#000'
  },
  tableHeaderCol: {
    padding: 2,
    fontSize: 6,
    fontWeight: 'bold',
    flexDirection: "column",
    justifyContent: 'center',
    alignItems: 'center',
    borderRightWidth: 1,
  },
  noRightBorder: {
    borderRightWidth: 0,
  },
  fontStyle: {
    textAlign: 'center'
  },
  footer: {
    position: "absolute",
    bottom: 26,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 6,
    color: '#000',
  },
  codeBox: {
    position: "absolute",
    top: -10,
    right: 10,
    flexDirection: "column",
    alignItems: "center",

  },
  codeImg: {
    flexGrow: 1,
    height: 30,
    width: 30
  },
  codeTxt: {
    fontSize: 6,
    textAlign: "center",
    marginTop: 2
  }
});

interface PrintInspectionProps {
  inspectionOrder: InspectionOrder;
  disabled?: boolean;
}

const PrintInspection: FC<PrintInspectionProps> = ({
  inspectionOrder,
  disabled = false
}) => {
  const { t } = useTranslation();
  const { getInspectionStatus, getExistEnum, getCompline } = useInspactionEnum(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { printSetting } = useContext(DictContext);

  // 获取公司logo
  const { picUrl, name } = useMemo(() => {
    let picUrl = '';
    let name = '';
    if (printSetting?.length > 0) {
      printSetting.forEach(item => {
        if (item.key === "companyLogo" && item.value) picUrl = getFileUrl(item.value) || '';
        if (item.key === "companyName" && item.value) name = item.value
      });
    }
    return { picUrl, name };
  }, [printSetting]);

  //生成二维码
  const handleQrCode = async (orderNo: string): Promise<{ codeUrl: string, codeStr: string }> => {
    try {
      let codeObjStr = codeObjToStr({ "in": orderNo, tt: TagType.INSPECTION });
      // 使用 QRCode.toDataURL 方法生成二维码图片的 Data URL
      const url = await QRCode.toDataURL(codeObjStr, {
        width: 100,
        margin: 0,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      return Promise.resolve({ codeStr: orderNo, codeUrl: url });
    } catch (err) {
      console.error(err);
      return Promise.reject('');
    }
  };

  const label = (key: string) => `${t(key, { lng: "zh" })}(${t(key, { lng: "vn" })})`;

  // 明细行
  const ItemRow = ({ item }: { item: InspectionOrderItem }) => {
    const exist = getExistEnum(item.existenceState);
    const compline = getCompline(item.complianceState);
    return (
      <View style={styles.tableRow} wrap={false}>
        <View style={[styles.tableCol, { width: '10%' }]}>
          <Text style={styles.fontStyle}>{getLocationIdx(item.itemLocation?.location) || item.locationCode || ''}</Text>
        </View>
        <View style={[styles.tableCol, { width: '12%' }]}>
          <Text style={styles.fontStyle}>{item.product?.code || ''}</Text>
        </View>
        <View style={[styles.tableCol, { width: '16%' }]}>
          <Text style={styles.fontStyle}>{item.product?.name || ''}</Text>
        </View>
        <View style={[styles.tableCol, { width: '8%' }]}>
          <Text style={styles.fontStyle}>{item.product?.uom || ''}</Text>
        </View>
        <View style={[styles.tableCol, { width: '12%' }]}>
          <Text style={styles.fontStyle}>{exist?.label || ''}</Text>
        </View>
        <View style={[styles.tableCol, { width: '12%' }]}>
          <Text style={styles.fontStyle}>{compline?.label || ''}</Text>
        </View>
        <View style={[styles.tableCol, { width: '14%' }]}>
          <Text style={styles.fontStyle}>{item.action || ''}</Text>
        </View>
        <View style={[styles.tableCol, { width: '16%' }]}>
          <Text style={styles.fontStyle}>{item.inspectedAt ? dateFormat(dayjs(item.inspectedAt)) : ''}</Text>
        </View>
        <View style={[styles.tableCol, styles.noRightBorder, { width: '10%' }]}>
          <Text style={styles.fontStyle}>{item.inspector ? getUserName(item.inspector) : ''}</Text>
        </View>
      </View>
    );
  };

  // 生成PDF文档
  const MyDocument = (items: InspectionOrderItem[], codeObj: {
    codeUrl: string;
    codeStr: string;
  }) => {
    return (
      <Document>
        <Page size="A4" orientation="portrait" style={styles.page}>
          {/* 标题栏 */}
          <View style={styles.titleSection}>
            <Text style={styles.title}>{name}</Text>
            <Text style={styles.subTitle}>{t('inspection.inspection', { lng: "zh" })}/{t('inspection.inspection', { lng: "vn" })}</Text>
            <View style={styles.codeBox}>
              <Image src={codeObj.codeUrl} style={styles.codeImg} />
              <Text style={styles.codeTxt}>{inspectionOrder.number}</Text>
            </View>
          </View>

          {/* 基本信息 */}
          <View style={styles.rowSection}>
            <Text style={styles.rowItem}>
              {`${label('inspection.planAt')}`}: {inspectionOrder.planAt ? dateFormat(dayjs(inspectionOrder.planAt)) : ''}
            </Text>
            <Text style={styles.rowItem}>
              {`${label('inspection.state')}`}: {getInspectionStatus(inspectionOrder.status)?.label || ''}
            </Text>
            <Text style={styles.rowItem}>
              {`${label('inspection.createdAt')}`}: {inspectionOrder.createdAt ? dateFormat(dayjs(inspectionOrder.createdAt)) : ''}
            </Text>
          </View>
          <View style={styles.rowSection}>
            <Text style={styles.rowItem}>
              {`${label('inspection.finishedAt')}`}: {inspectionOrder.finishedAt ? dateFormat(dayjs(inspectionOrder.finishedAt)) : ''}
            </Text>
            <Text style={styles.rowItem} />
            <Text style={styles.rowItem} />
          </View>
          <View style={styles.rowSection}>
            <Text style={styles.rowItem}>
              {`${label('inspection.remark')}`}: {inspectionOrder.remark || ''}
            </Text>
            <Text style={styles.rowItem} />
          </View>

          {/* 巡检明细表格 */}
          <View style={styles.table}>
            <View style={styles.tableRow} wrap={false}>
              <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                <Text>{t('inspection.locationCode', { lng: "zh" })}</Text>
                <Text>{t('inspection.locationCode', { lng: "vn" })}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '12%' }]}>
                <Text>{t('product.code', { lng: "zh" })}</Text>
                <Text>{t('product.code', { lng: "vn" })}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '16%' }]}>
                <Text>{t('product.name', { lng: "zh" })}</Text>
                <Text>{t('product.name', { lng: "vn" })}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '8%' }]}>
                <Text>{t('product.uom', { lng: "zh" })}</Text>
                <Text>{t('product.uom', { lng: "vn" })}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '12%' }]}>
                <Text>{t('inspection.existenceState', { lng: "zh" })}</Text>
                <Text>{t('inspection.existenceState', { lng: "vn" })}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '12%' }]}>
                <Text>{t('inspection.complianceState', { lng: "zh" })}</Text>
                <Text>{t('inspection.complianceState', { lng: "vn" })}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '14%' }]}>
                <Text>{t('inspection.action', { lng: "zh" })}</Text>
                <Text>{t('inspection.action', { lng: "vn" })}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '16%' }]}>
                <Text>{t('inspection.inspectedAt', { lng: "zh" })}</Text>
                <Text>{t('inspection.inspectedAt', { lng: "vn" })}</Text>
              </View>
              <View style={[styles.tableHeaderCol, styles.noRightBorder, { width: '10%' }]}>
                <Text>{t('inspection.inspector', { lng: "zh" })}</Text>
                <Text>{t('inspection.inspector', { lng: "vn" })}</Text>
              </View>
            </View>
            {items?.map((item) => <ItemRow key={item.id} item={item} />)}
          </View>

          {/* Logo图片 */}
          <Image src={picUrl || zioncom} style={{
            position: "absolute",
            top: 24,
            left: 30,
            flexGrow: 1,
            height: 30,
            objectFit: "scale-down"
          }} />
          {/* 页脚 */}
          <Text style={styles.footer} render={({ pageNumber, totalPages }) =>
            `P ${pageNumber} / ${totalPages}`
          } fixed />
        </Page>
      </Document>
    );
  };

  // 处理PDF打印
  const handlePDF = async () => {
    if (!inspectionOrder.items || inspectionOrder.items.length === 0) {
      return notification.error({
        placement: 'top',
        message: t('apiError.error3'),
        description: '空的巡检单无法打印'
      });
    }

    setLoading(true);

    let codeObj = await handleQrCode(inspectionOrder.number);

    try {
      // 生成PDF文档
      const pageElement = MyDocument(inspectionOrder.items, codeObj);
      if (!pageElement) {
        notification.error({ placement: 'top', message: t('requirement.documentGenerationFailed') });
        setLoading(false);
        return;
      }

      // 将文档转换为Blob
      const blob = await pdf(pageElement).toBlob();
      const pdfUrl = URL.createObjectURL(blob);

      // 创建隐藏的iframe用于打印
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = pdfUrl;

      // 将iframe添加到body中
      document.body.appendChild(iframe);

      iframe.onload = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (error) {
          console.error('打印失败:', error);
          notification.error({ placement: 'top', message: t('requirement.printFailed') });
        } finally {
          setLoading(false);
        }
      };

    } catch (error) {
      console.error('生成PDF失败:', error);
      notification.error({ placement: 'top', message: t('requirement.pdfGenerationFailed') });
      setLoading(false);
    }
  };

  return (
    <Button
      type="primary"
      icon={<PrinterOutlined />}
      onClick={handlePDF}
      loading={loading}
      disabled={disabled || !inspectionOrder.items?.length}
    >
      {t('common.printInfo')}
    </Button>
  );
};

export default PrintInspection;
